import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, FileText, Brain, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Pagination } from '@/components/ui/pagination'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/services/api/client'
import { exportToCSV, exportToJSON } from '@/utils/export'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import { useTranslation } from 'react-i18next'
import ResultsChart from '@/components/results/ResultsChart'
import { cleanColumnNames } from '@/utils/column-names'
import AIResultsExplanation from '@/components/ai/AIResultsExplanation'
import { jobsApi } from '@/services/api/jobs'
import { scriptsApi } from '@/services/api/scripts'
import { datasetsApi } from '@/services/api/datasets'
import { isDataTooLargeForAI } from '@/utils/data-size'

interface Result {
  id: string
  jobId: string
  resultType: 'table' | 'stats' | 'signals' | 'backtest'
  data?: Record<string, unknown> | any[]
  storagePath?: string | null
  createdAt: string
  metadata?: {
    columns?: string[]
    executionTimeMs?: number
    hasMore?: boolean
    compiledSql?: string
  }
}

export default function ResultsPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isAIAnalysisOpen, setIsAIAnalysisOpen] = useState(false)
  const [showAllRows, setShowAllRows] = useState(false)
  const [showAllText, setShowAllText] = useState(false)
  
  // Paginated signals state
  const [signalsData, setSignalsData] = useState<any[]>([])
  const [signalsPage, setSignalsPage] = useState(1)
  const [signalsPageSize, setSignalsPageSize] = useState(100)
  const [signalsHasMore, setSignalsHasMore] = useState(false)
  const [signalsTotalPages, setSignalsTotalPages] = useState<number | null>(null)
  const [signalsTotalItems, setSignalsTotalItems] = useState<number | null>(null)
  const [isLoadingSignals, setIsLoadingSignals] = useState(false)
  const [signalsColumns, setSignalsColumns] = useState<string[]>([])

  const { data: result, isLoading, error } = useQuery<Result>({
    queryKey: ['results', id],
    queryFn: async () => {
      const response = await apiClient.get(`/jobs/${id}/result`)
      return response.data as Result
    },
    enabled: !!id,
  })
  
  // Fetch signals via paginated API when result is signals type
  const { data: signalsResponse } = useQuery({
    queryKey: ['signals', result?.jobId, signalsPage, signalsPageSize],
    queryFn: async () => {
      if (!result?.jobId || result?.resultType !== 'signals') return null
      return await jobsApi.getSignals(result.jobId, signalsPage, signalsPageSize)
    },
    enabled: !!result?.jobId && !!result && result.resultType === 'signals',
  })

  // Initialize signals data from API response
  useEffect(() => {
    if (signalsResponse?.success) {
      setSignalsData(signalsResponse.data)
      setSignalsColumns(signalsResponse.columns || [])
      setSignalsHasMore(signalsResponse.hasMore)
      setSignalsTotalPages(signalsResponse.totalPages)

      // Calculate total items based on available information
      if (signalsResponse.totalPages !== null && signalsResponse.totalPages > 0) {
        // If we know total pages, calculate total items
        const totalItems = signalsResponse.totalPages * signalsPageSize
        setSignalsTotalItems(totalItems)
      } else if (signalsResponse.hasMore) {
        // If we don't know total pages but there are more, estimate conservatively
        const currentItems = (signalsPage - 1) * signalsPageSize + signalsResponse.data.length
        setSignalsTotalItems(currentItems + signalsPageSize) // At least one more page
      } else {
        // Last page, calculate exact total
        const totalItems = (signalsPage - 1) * signalsPageSize + signalsResponse.data.length
        setSignalsTotalItems(totalItems)
      }
    } else if (result?.resultType !== 'signals') {
      // Reset signals state when result is not signals
      setSignalsData([])
      setSignalsColumns([])
      setSignalsHasMore(false)
      setSignalsTotalPages(null)
      setSignalsTotalItems(null)
      setSignalsPage(1)
    }
  }, [signalsResponse, result?.resultType, signalsPageSize, signalsPage])

  // Handle page change
  const handleSignalsPageChange = (page: number) => {
    setSignalsPage(page)
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Handle page size change
  const handleSignalsPageSizeChange = (pageSize: number) => {
    setSignalsPageSize(pageSize)
    setSignalsPage(1) // Reset to first page
  }
  
  // Handle errors
  useEffect(() => {
    if (error) {
      showErrorToast(error as Error, t('errors.loadJob'))
    }
  }, [error, t])

  // Fetch job information for context
  const { data: job } = useQuery({
    queryKey: ['jobs', result?.jobId],
    queryFn: async () => {
      if (!result?.jobId) return null
      return await jobsApi.get(result.jobId)
    },
    enabled: !!result?.jobId,
  })

  // Fetch script information for context
  const { data: script } = useQuery({
    queryKey: ['scripts', job?.scriptId],
    queryFn: async () => {
      if (!job?.scriptId) return null
      return await scriptsApi.get(job.scriptId)
    },
    enabled: !!job?.scriptId,
  })

  // Fetch dataset information for context
  const { data: dataset } = useQuery({
    queryKey: ['datasets', job?.datasetId],
    queryFn: async () => {
      if (!job?.datasetId) return null
      return await datasetsApi.get(job.datasetId)
    },
    enabled: !!job?.datasetId,
  })

  const handleExport = (format: 'csv' | 'json') => {
    if (!result) return

    try {
      if (format === 'csv') {
        if (Array.isArray(result.data)) {
          exportToCSV(result.data, `results-${id}-${Date.now()}.csv`)
          showSuccessToast(t('results.exportCSVSuccess'))
        } else {
          showErrorToast(new Error(t('results.exportCSVError')), t('errors.generic'))
        }
      } else {
        exportToJSON(result.data || result, `results-${id}-${Date.now()}.json`)
        showSuccessToast(t('results.exportJSONSuccess'))
      }
    } catch (error) {
      showErrorToast(error, t('errors.generic'))
    }
  }

  // Check if data is too large for AI analysis
  const dataSizeCheck = useMemo(() => {
    if (!result?.data) return { tooLarge: false };
    return isDataTooLargeForAI(result.data);
  }, [result?.data]);

  // Reset showAllRows when result changes
  useEffect(() => {
    setShowAllRows(false);
  }, [result?.id]);

  if (isLoading) {
    return <LoadingPage message={t('common.loading')} />
  }

  if (!result) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('errors.resultNotFound')}</p>
        <Button onClick={() => navigate('/jobs')} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{t('results.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t(`results.resultType.${result.resultType}`)} - {t('results.jobId')} {result.jobId.slice(0, 8)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {result.data && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="default"
                      onClick={() => setIsAIAnalysisOpen(true)}
                      disabled={dataSizeCheck.tooLarge}
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      {t('results.analyzeWithAI')}
                    </Button>
                  </span>
                </TooltipTrigger>
                {dataSizeCheck.tooLarge && (
                  <TooltipContent>
                    <p className="max-w-xs">{dataSizeCheck.reason}</p>
                    <p className="text-xs mt-1">{t('results.aiAnalysisTooLarge', 'Data is too large for AI analysis. Please reduce the dataset size.')}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          {result.data && Array.isArray(result.data) && (
            <Button variant="outline" onClick={() => handleExport('csv')}>
              <Download className="w-4 h-4 mr-2" />
              {t('results.exportCSV')}
            </Button>
          )}
          <Button variant="outline" onClick={() => handleExport('json')}>
            <Download className="w-4 h-4 mr-2" />
            {t('results.exportJSON')}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('results.resultInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('results.resultTypeLabel')}</span>
              <span className="text-sm font-medium">{t(`results.resultType.${result.resultType}`) || result.resultType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('results.jobId')}</span>
              <span className="text-sm font-mono">{result.jobId}</span>
            </div>
            {result.storagePath && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">{t('results.storagePath')}</span>
                <span className="text-sm font-mono text-xs break-all">{result.storagePath}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {result.data ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('results.data')}</CardTitle>
              <CardDescription>
                {result.resultType === 'table' && t('results.tableView')}
                {result.resultType === 'stats' && t('results.statisticalSummary')}
                {result.resultType === 'signals' && t('results.generatedSignals')}
                {result.resultType === 'backtest' && t('results.backtestResults')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {result.resultType === 'table' && Array.isArray(result.data) ? (
                <>
                  {/* Check if data looks like error messages */}
                  {(() => {
                    const looksLikeError = result.data.length === 1 &&
                      typeof result.data[0] === 'string' &&
                      (result.data[0].includes('Error:') ||
                       result.data[0].includes('Type error:') ||
                       result.data[0].includes('Failed to compile'));

                    if (looksLikeError) {
                      // Show error message instead of table
                      return (
                        <Card className="border-destructive/50">
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                              <div className="text-destructive text-xl">⚠️</div>
                              <div>
                                <h3 className="font-semibold text-destructive mb-2">
                                  {t('results.executionError', 'Execution Error')}
                                </h3>
                                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                  {result.data[0]}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    // Continue with normal rendering
                    return (
                      <>
                        {/* Chart visualization for numeric data */}
                        {result.data.length > 0 &&
                         Object.values(result.data[0] || {}).some((v) => typeof v === 'number') && (
                          <ResultsChart
                            data={result.data}
                            type="line"
                            title={t('results.chartView')}
                            description={t('results.chartDescription')}
                          />
                        )}

                        {/* Table view */}
                        {(() => {
                          // Clean column names to remove hash suffixes
                          const cleanedData = cleanColumnNames(result.data);
                          const hasMoreThan500 = cleanedData.length > 500;
                          const displayRows = hasMoreThan500 && !showAllRows 
                            ? cleanedData.slice(0, 500) 
                            : cleanedData;
                          const shouldShowToggle = hasMoreThan500;
                          
                          return (
                            <div className="space-y-4">
                              {shouldShowToggle && (
                                <div className="flex items-center justify-between">
                                  <p className="text-sm text-muted-foreground">
                                    {showAllRows 
                                      ? t('results.showingAllRows', { total: cleanedData.length })
                                      : t('results.showingRows', { count: 500, total: cleanedData.length })
                                    }
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowAllRows(!showAllRows)}
                                    className="flex items-center gap-2"
                                  >
                                    {showAllRows ? (
                                      <>
                                        <ChevronUp className="w-4 h-4" />
                                        {t('results.hideRows', 'Hide Rows')}
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="w-4 h-4" />
                                        {t('results.showAllRows', 'Show All Rows')}
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                  <thead>
                                    <tr className="border-b">
                                      {Object.keys(cleanedData[0] || {}).map((key) => (
                                        <th key={key} className="text-left p-2 font-semibold">
                                          {key}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {displayRows.map((row: Record<string, any>, idx: number) => (
                                      <tr key={idx} className="border-b hover:bg-muted/50">
                                        {Object.values(row).map((value, colIdx) => (
                                          <td key={colIdx} className="p-2 text-sm">
                                            {String(value)}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {!shouldShowToggle && cleanedData.length > 100 && (
                                  <p className="text-sm text-muted-foreground mt-4">
                                    {t('results.showingRows', { count: 100, total: cleanedData.length })}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </>
              ) : result.resultType === 'signals' ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t('results.generatedSignals')}
                        {signalsTotalItems !== null && (
                          <span className="ml-2 text-sm font-normal text-muted-foreground">
                            ({signalsTotalItems}{signalsHasMore ? '+' : ''} signals)
                          </span>
                        )}
                      </h3>
                    </div>
                  </div>
                  {signalsData.length > 0 ? (
                    <>
                      <div className="border rounded-md overflow-auto max-h-[600px]">
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              {signalsColumns.length > 0 ? (
                                signalsColumns.map((key) => (
                                  <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                ))
                              ) : (
                                Object.keys(signalsData[0] as Record<string, unknown>).map((key) => (
                                  <th key={key} className="px-4 py-2 text-left font-medium">{key}</th>
                                ))
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {signalsData.map((row: any, idx: number) => (
                              <tr key={idx} className="border-t hover:bg-muted/50">
                                {signalsColumns.length > 0 ? (
                                  signalsColumns.map((col) => (
                                    <td key={col} className="px-4 py-2">
                                      {typeof row[col] === 'number' 
                                        ? row[col].toLocaleString() 
                                        : String(row[col] ?? '')}
                                    </td>
                                  ))
                                ) : (
                                  Object.values(row).map((value: any, colIdx: number) => (
                                    <td key={colIdx} className="px-4 py-2">
                                      {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                    </td>
                                  ))
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {signalsTotalPages !== null && signalsTotalPages > 1 && (
                        <Pagination
                          currentPage={signalsPage}
                          totalPages={signalsTotalPages}
                          pageSize={signalsPageSize}
                          totalItems={signalsTotalItems || undefined}
                          onPageChange={handleSignalsPageChange}
                          onPageSizeChange={handleSignalsPageSizeChange}
                          showPageSizeSelector={true}
                          pageSizeOptions={[25, 50, 100, 250, 500, 1000]}
                          className="mt-4"
                        />
                      )}
                      {signalsTotalPages === null && signalsHasMore && (
                        <div className="p-4 text-center border-t">
                          <Button
                            variant="outline"
                            onClick={async () => {
                              if (!result?.jobId || isLoadingSignals || !signalsHasMore) return
                              setIsLoadingSignals(true)
                              try {
                                const nextPage = signalsPage + 1
                                const response = await jobsApi.getSignals(result.jobId, nextPage, signalsPageSize)
                                if (response.success && response.data.length > 0) {
                                  setSignalsData(response.data) // Replace data instead of accumulating
                                  setSignalsPage(nextPage)
                                  setSignalsHasMore(response.hasMore)
                                  if (response.columns.length > 0 && signalsColumns.length === 0) {
                                    setSignalsColumns(response.columns)
                                  }
                                } else {
                                  setSignalsHasMore(false)
                                }
                              } catch (error) {
                                console.error('Failed to load more signals:', error)
                                showErrorToast(error as Error, 'Failed to load more signals')
                                setSignalsHasMore(false)
                              } finally {
                                setIsLoadingSignals(false)
                              }
                            }}
                            disabled={isLoadingSignals}
                          >
                            {isLoadingSignals ? (
                              <>
                                <ChevronDown className="mr-2 h-4 w-4 animate-spin" />
                                Loading more signals...
                              </>
                            ) : (
                              <>
                                <ChevronDown className="mr-2 h-4 w-4" />
                                Load More (Page {signalsPage + 1})
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No signals found</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const jsonString = JSON.stringify(result.data, null, 2);
                    const maxChars = 50000; // ~50KB of text
                    const isLarge = jsonString.length > maxChars;
                    const displayText = isLarge && !showAllText 
                      ? jsonString.substring(0, maxChars) + '\n\n... (content truncated) ...'
                      : jsonString;
                    
                    return (
                      <>
                        {isLarge && (
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                              {showAllText 
                                ? t('results.showingAllContent', { size: `${(jsonString.length / 1024).toFixed(1)}KB` })
                                : t('results.showingPartialContent', { 
                                    shown: `${(maxChars / 1024).toFixed(1)}KB`, 
                                    total: `${(jsonString.length / 1024).toFixed(1)}KB` 
                                  })
                              }
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowAllText(!showAllText)}
                              className="flex items-center gap-2"
                            >
                              {showAllText ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  {t('results.hideContent', 'Hide Content')}
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  {t('results.showAllContent', 'Show All Content')}
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                        <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-[600px]">
                          {displayText}
                        </pre>
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        ) : result.storagePath ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {t('results.storedExternally')}
              </p>
              <Button variant="outline" onClick={() => handleExport('json')}>
                <Download className="w-4 h-4 mr-2" />
                {t('results.downloadResults')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground">{t('results.noDataAvailable')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* AI Analysis Dialog */}
      <Dialog open={isAIAnalysisOpen} onOpenChange={setIsAIAnalysisOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              {t('results.aiAnalysis')}
            </DialogTitle>
            <DialogDescription>
              {t('results.aiAnalysisDescription')}
            </DialogDescription>
          </DialogHeader>
          {result.data && (
            <AIResultsExplanation
              results={
                Array.isArray(result.data)
                  ? { data: result.data, count: result.data.length }
                  : (result.data as Record<string, any>)
              }
              scriptDescription={script?.description || script?.name}
              scriptCode={script?.dslCode}
              datasetInfo={{
                symbol: dataset?.metadata?.symbol as string | undefined,
                timeframe: dataset?.metadata?.timeframe as string | undefined,
                columns: dataset?.metadata?.columns as string[] | undefined,
                timezone: (script?.metadata as Record<string, unknown> | null)?.timezone as string | undefined ?? dataset?.metadata?.timezone as string | undefined,
                sessionStrategy: (script?.metadata as Record<string, unknown> | null)?.session_strategy as 'by_date' | 'custom' | 'trading_sessions' | undefined ?? dataset?.metadata?.session_strategy as 'by_date' | 'custom' | 'trading_sessions' | undefined,
                sessions: ((script?.metadata as Record<string, unknown> | null)?.sessions as Array<{ name: string; start_time: string; end_time: string; description?: string }> | undefined) ?? dataset?.metadata?.sessions as Array<{
                  name: string;
                  start_time: string;
                  end_time: string;
                  description?: string;
                }> | undefined,
                sessionIncludeDate: dataset?.metadata?.session_include_date as boolean | undefined,
                intervalDuration: (script?.metadata as Record<string, unknown> | null)?.interval_duration as number | string | undefined ?? dataset?.metadata?.interval_duration as number | string | undefined,
                intervalIdFormat: (script?.metadata as Record<string, unknown> | null)?.interval_id_format as 'timestamp' | 'datetime' | undefined ?? dataset?.metadata?.interval_id_format as 'timestamp' | 'datetime' | undefined,
              }}
              resultType={result.resultType}
              scriptId={script?.id}
              resultId={result.id}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
